/* Rhody Reviews api/v1 -- social domain. PURE EXTRACTION from lib/api.ts (gate-prep split); no behavior change. */
import { request } from './_http';

// -------------------------------------------------------------------
// Social posting layer (2026-06-17) -- in-house feature. DRY-RUN this
// wave: posts are stored, never published to Meta (gated on a Meta
// developer app + App Review). All endpoints rr_ token + tenant-scoped.
// -------------------------------------------------------------------
export type SocialPlatform = 'facebook' | 'instagram';
export type SocialPostType = 'feed' | 'story';
export type SocialPostStatus = 'draft' | 'scheduled' | 'posted' | 'failed';

export interface SocialPlatformNeeds {
  provider: string;
  label: string;
  app_name: string;
  app_type: string;
  product: string;
  redirect_uri: string;
  scopes: string[];
  app_id_env: string;
  app_secret_env: string;
  app_review_required: boolean;
  register_at: string;
}
export interface SocialPlatformStatus {
  provider: SocialPlatform;
  label: string;
  description: string;
  configured: boolean;
  needs: SocialPlatformNeeds | null;
  connect_url: string | null;
}
export interface SocialPlatformsResponse {
  client_id: string;
  platforms: SocialPlatformStatus[];
}

export interface SocialConnection {
  id: string;
  platform: SocialPlatform;
  account_id: string | null;
  account_name: string | null;
  location_label: string | null;
  status: 'connected' | 'disconnected' | 'error';
  account_meta: Record<string, unknown>;
  connected_at: string | null;
}
export interface SocialConnectionsResponse {
  client_id: string;
  count: number;
  connections: SocialConnection[];
}

/* The composer design state persisted with a post so a draft reopens
   exactly as composed. All fields optional; the editor fills what it uses. */
export interface SocialPostDesign {
  review_id?: string;
  photo_ref?: string; // data URL of the uploaded background photo
  logo_ref?: string; // data URL of the uploaded logo/watermark
  card_scale?: number;
  card_x?: number;
  card_y?: number;
  photo_orientation?: 'horizontal' | 'vertical';
  photo_scale?: number;
  bg_color?: string;
  accent_color?: string;
  text_color?: string;
  aspect?: string; // '1:1' (feed) | '9:16' (story)
  [k: string]: unknown;
}

export interface ScheduledPost {
  id: string;
  social_connection_id: string | null;
  platform: SocialPlatform;
  location_label: string | null;
  post_type: SocialPostType;
  caption: string;
  design: SocialPostDesign;
  rendered_image_ref: string | null;
  scheduled_at: string | null;
  status: SocialPostStatus;
  position: number;
  posted_at: string | null;
  last_error: string | null;
  created_at: string | null;
  updated_at: string | null;
}
export interface ScheduledPostsResponse {
  client_id: string;
  count: number;
  posts: ScheduledPost[];
}
export interface ScheduledPostInput {
  platform: SocialPlatform;
  post_type?: SocialPostType;
  caption?: string;
  scheduled_at?: string | null;
  social_connection_id?: string | null;
  location_label?: string | null;
  design?: SocialPostDesign;
  rendered_image_ref?: string | null;
}
export type ScheduledPostPatch = Partial<ScheduledPostInput>;
export interface ScheduledPostCreated {
  client_id: string;
  post: ScheduledPost;
  dry_run: boolean;
}
export interface ScheduledPostResult {
  client_id: string;
  post: ScheduledPost;
}
export interface ReorderPostsResult {
  client_id: string;
  reordered: number;
  posts: ScheduledPost[];
}

export const socialApi = {
  /* ----- Social posting (DRY-RUN: never publishes to Meta this wave) ----- */
  listSocialPlatforms(signal?: AbortSignal): Promise<SocialPlatformsResponse> {
    return request<SocialPlatformsResponse>('/api/v1/social/platforms', { signal });
  },
  listSocialConnections(signal?: AbortSignal): Promise<SocialConnectionsResponse> {
    return request<SocialConnectionsResponse>('/api/v1/social/connections', { signal });
  },
  disconnectSocialConnection(
    id: string,
  ): Promise<{ client_id: string; disconnected: string }> {
    return request<{ client_id: string; disconnected: string }>(
      `/api/v1/social/connections/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    );
  },
  listScheduledPosts(
    params: { status?: SocialPostStatus } = {},
    signal?: AbortSignal,
  ): Promise<ScheduledPostsResponse> {
    return request<ScheduledPostsResponse>('/api/v1/social/posts', {
      query: { status: params.status },
      signal,
    });
  },
  createScheduledPost(input: ScheduledPostInput): Promise<ScheduledPostCreated> {
    return request<ScheduledPostCreated>('/api/v1/social/posts', {
      method: 'POST',
      body: input,
    });
  },
  updateScheduledPost(
    id: string,
    patch: ScheduledPostPatch,
  ): Promise<ScheduledPostResult> {
    return request<ScheduledPostResult>(
      `/api/v1/social/posts/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: patch },
    );
  },
  deleteScheduledPost(id: string): Promise<{ client_id: string; deleted: string }> {
    return request<{ client_id: string; deleted: string }>(
      `/api/v1/social/posts/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    );
  },
  reorderScheduledPosts(order: string[]): Promise<ReorderPostsResult> {
    return request<ReorderPostsResult>('/api/v1/social/posts/reorder', {
      method: 'POST',
      body: { order },
    });
  },
};
